import { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Camera, X, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function PhotoUpload({ photos = [], onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    if (photos.length >= 10) {
      toast.error('Maximum 10 photos allowed');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onUpdate?.([...photos, res.data.photo_url]);
      toast.success('Photo uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (index) => {
    try {
      await api.delete(`/photos/${index}`);
      const updated = photos.filter((_, i) => i !== index);
      onUpdate?.(updated);
      toast.success('Photo removed');
    } catch (err) {
      toast.error('Failed to delete photo');
    }
  };

  return (
    <div data-testid="photo-upload">
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {photos.map((photo, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-[#E7E5E4] group">
            <img src={photo} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
            <button
              onClick={() => handleDelete(i)}
              className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`delete-photo-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}

        {photos.length < 10 && (
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="aspect-square rounded-xl border-2 border-dashed border-[#E7E5E4] hover:border-[#D9381E] flex flex-col items-center justify-center gap-1 transition-colors cursor-pointer disabled:opacity-50"
            data-testid="upload-photo-btn"
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 text-[#D9381E] animate-spin" />
            ) : (
              <>
                <Camera className="w-6 h-6 text-[#78716C]" />
                <span className="text-[10px] text-[#78716C]">Add Photo</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleUpload}
        className="hidden"
        data-testid="photo-file-input"
      />
      <p className="text-xs text-[#78716C] mt-2">{photos.length}/10 photos. Max 5MB each. JPEG, PNG, WebP.</p>
    </div>
  );
}
